import { FormProps } from '@rjsf/core';
import { WidgetProps } from '@rjsf/utils';
import React from 'react';
import Form from '@rjsf/mui';
import { CardActions } from '@bb-ui/react-library/dist/components/CardActions';
import {
  CardContent,
  Chip,
  Input,
  IconButton,
  InputLabel,
  Tooltip,
  Box,
  createStyles,
  makeStyles,
} from '@bb-ui/react-library';
import { BulletedListDefault, Editor } from '@bb-ui/icons/dist/small';
import ReactJson from 'react-json-view';
import { JobCard, JobCardProps } from './JobCard';
import { Paste, CheckCircle } from '@bb-ui/icons/dist/medium';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme) =>
  createStyles({
    copyableWrapper: {
      display: 'flex',
      alignItems: 'center',
      minWidth: 0,
    },
    urlLink: {
      textDecoration: 'none',
      flex: 1,
      minWidth: 0,
      display: 'flex',
    },
    urlBox: {
      padding: theme.spacing(0.5, 1),
      border: `1px solid ${theme.palette.divider}`,
      background: theme.palette.background.default,
      color: theme.palette.text.secondary,
      fontFamily: 'inherit',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      borderRadius: 4,
      fontSize: '13px',
    },
    urlText: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: 'block',
    },
    copyButton: {
      marginLeft: theme.spacing(0.5),
      padding: 6,
    },
    cardContent: {
      padding: '4px 12px 8px',
      '&:last-child': {
        paddingBottom: 8,
      },
      '& .MuiFormControl-root': {
        marginBottom: 4,
      },
      '& .MuiInputLabel-root': {
        fontSize: '12px',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.3px',
        color: 'rgba(0, 0, 0, 0.45)',
      },
      '& .MuiInputBase-input': {
        fontSize: '13px',
        padding: '4px 0',
      },
      '& fieldset': {
        marginBottom: 0,
      },
    },
    cardActions: {
      padding: '4px 12px 8px',
    },
  }),
);

// List of attribute names to exclude from UI display due to sensitivity
const INGORE_ATTRIBUTES = ['password'];
const COPYABLE_ATTRIBUTES = ['TenantId', 'url', 'entryCode'];

export interface JobFormCardProps extends Omit<JobCardProps, 'content'>, FormProps<any> {}

const isLabelCopyable = (label: string): boolean => COPYABLE_ATTRIBUTES.includes(label);

const CopyableTextWidget: React.FC<WidgetProps> = ({ id, label, value, readonly }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [isCopied, setCopied] = React.useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(value || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div>
      <InputLabel>{label}</InputLabel>
      <div className={classes.copyableWrapper}>
        {label === 'url' ? (
          <a href={value} target="_blank" rel="noopener noreferrer" className={classes.urlLink}>
            <Box className={classes.urlBox}>
              <Tooltip
                title={t('jobService.urlFieldTooltip').toString()}
                placement="top"
                data-testid="fleets-dialog-schedule-date"
              >
                <span className={classes.urlText}>{value}</span>
              </Tooltip>
            </Box>
          </a>
        ) : (
          <Input id={id} value={value} style={{ flex: 1, minWidth: 0 }} disabled={readonly} />
        )}
        {isLabelCopyable(label) && (
          <IconButton type="button" onClick={handleCopy} className={classes.copyButton}>
            {isCopied ? (
              <CheckCircle />
            ) : (
              <Tooltip
                title={t('jobService.copyFieldTooltip', { label }).toString()}
                placement="top"
                data-testid="fleets-dialog-schedule-date"
              >
                <Paste />
              </Tooltip>
            )}
          </IconButton>
        )}
      </div>
    </div>
  );
};

export const JobFormCard: React.FC<JobFormCardProps> = (props) => {
  const classes = useStyles();
  const [rawShown, setRawShown] = React.useState(false);
  const { uiSchema, formData, actions, ...rest } = props;

  const updatedUiSchema = Object.keys(formData || {}).reduce((acc: { [key: string]: any }, key) => {
    if (isLabelCopyable(key)) acc[key] = { 'ui:widget': 'copyableText' };
    return acc;
  }, {});

  // Remove sensitive information from formData
  const reactJsonFormData = Object.keys(formData || {})
    .filter((key: string) => !INGORE_ATTRIBUTES.includes(key))
    .reduce(
      (acc, key: string) => {
        acc[key] = formData[key];
        return acc;
      },
      {} as Record<string, any>,
    );

  return (
    <JobCard
      content={
        <CardContent className={classes.cardContent}>
          {rawShown ? (
            <ReactJson src={reactJsonFormData} name={false} enableClipboard={true} />
          ) : (
            <Form
              uiSchema={{
                ...uiSchema,
                ...updatedUiSchema,
                'ui:submitButtonOptions': { norender: true },
                'ui:readonly': true,
              }}
              {...rest}
              formData={formData}
              widgets={{ copyableText: CopyableTextWidget }}
            />
          )}
        </CardContent>
      }
      actions={
        <CardActions className={classes.cardActions}>
          <Chip
            label={`View ${rawShown ? 'List' : 'JSON'}`}
            onClick={() => setRawShown(!rawShown)}
            icon={rawShown ? <BulletedListDefault /> : <Editor />}
          />
          {actions}
        </CardActions>
      }
      {...rest}
    />
  );
};
